/**
 * Whether a session on a given host can read an account's credentials.
 *
 * The mirror of `domain.AccountProfile.ReachableFrom`, and the browser's only
 * copy of it. CLAUDE.md says that method is the one place the rule lives; a
 * screen that works it out again is a second place, and the second place is the
 * one that gets it wrong. It did: the switch-account dialog filtered on
 * `hostId === session.hostId` alone and so hid every account on shared storage,
 * telling an operator there was nowhere for the work to go while every account
 * was in fact reachable from every host.
 *
 * Three ways it is true, and they are not the same claim:
 *
 *   - the directory is on storage the fleet shares (ADR-0013), so it resolves
 *     everywhere;
 *   - the session runs on the host that owns the directory (ADR-0006);
 *   - the session runs on the control host — compatibility rather than a fact
 *     about the filesystem, kept because removing it would start refusing
 *     launches that have always worked.
 *
 * Kept as a function rather than inlined at each call site for the reason the Go
 * one is a method: the exception has to live somewhere it can be read.
 */

import type { ID } from '@/types/domain'

export interface ReachableAccount {
  /** The directory is on storage every host mounts. */
  sharedStorage?: boolean
  /** The host that owns the directory, when one does. */
  hostId?: ID
}

export interface ReachableHost {
  id: ID
  isControlHost?: boolean
}

export function accountReachableFrom(
  account: ReachableAccount,
  host: ReachableHost | undefined,
): boolean {
  // Nothing known about the host is not a licence to guess. Saying "reachable"
  // here would put an account in front of an operator that the server may then
  // refuse, after it had already stopped their session.
  if (!host) return false
  if (account.sharedStorage) return true
  if (host.isControlHost) return true
  return account.hostId === host.id
}
